import React, { useState } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../utils/axiosConfig';
import FiyatKontrolUyari from './FiyatKontrolUyari';
import FiyatAraliklariModal from './FiyatAraliklariModal';
import { FiyatlandirmaKurali } from '../utils/fiyatlandirmaKontrol';
import type { PlanKalemi } from './OdemePlaniOlusturucu';
import { planDurumu } from './OdemePlaniOlusturucu';

// v1.27.0 — AI destekli ödeme planı sihirbazı: 1) planı yazıyla tarif et, 2) eksik bilgileri cevapla, 3) önizle ve uygula.
// Sunucu metni yapısal tarife çevirir (AI ya da yerel ayrıştırıcı); kalemler sunucuda hesaplanır, toplam sözleşme fiyatına eşittir.

interface Soru { alan: string; soru: string; tip: 'sayi' | 'tutar' | 'secim'; secenekler: string[]; varsayilan?: string | null; zorunlu: boolean; }
interface Sonuc {
    kaynak: 'ai' | 'kural'; aiAktif: boolean; aciklama: string | null; hata?: string;
    tarif: unknown; sorular: Soru[]; varsayimlar: string[];
    kalemler: { vadeTarihi: string; miktar: number; odemeTuru: string; aciklama: string }[] | null;
}

const ORNEKLER = [
    '%30 peşin, kalanı 24 ay senetle',
    '1 milyon peşinat, 6. ayda 500 bin ve 12. ayda 500 bin ara ödeme, kalanı 18 taksit',
    '%20 peşin, kalanı 3 ayda bir olmak üzere 8 taksit, çek ile',
    'peşinatsız, aylık 150 bin taksit',
];
const TUR_ADI: Record<string, string> = { Nakit: 'Nakit', BankaHavale: 'Banka havalesi', Senet: 'Senet', Cek: 'Çek', Takas: 'Takas' };
const tl = (n: number) => `${Math.round(n).toLocaleString('tr-TR')} ₺`;
const tarihTr = (iso: string) => { const [y, m, d] = iso.split('-'); return `${d}.${m}.${y}`; };

const AiOdemePlaniSihirbazi: React.FC<{
    toplam: number;
    sozlesmeTarihi: string;
    listeFiyati?: number | null;
    kural: FiyatlandirmaKurali | null;
    kullaniciID?: number | null;
    mevcutKalemSayisi: number;
    onUygula: (k: PlanKalemi[]) => void;
    onKapat: () => void;
}> = ({ toplam, sozlesmeTarihi, listeFiyati, kural, kullaniciID, mevcutKalemSayisi, onUygula, onKapat }) => {
    const [adim, setAdim] = useState<1 | 2 | 3>(1);
    const [metin, setMetin] = useState('');
    const [sonuc, setSonuc] = useState<Sonuc | null>(null);
    const [cevaplar, setCevaplar] = useState<Record<string, string>>({});
    const [yukleniyor, setYukleniyor] = useState(false);
    const [araliklarAcik, setAraliklarAcik] = useState(false);

    const sorgula = async (devam: boolean) => {
        setYukleniyor(true);
        try {
            const r = await axiosInstance.post('/api/OdemePlaniAsistani/yorumla', {
                metin, toplam, sozlesmeTarihi, listeFiyati: listeFiyati ?? null,
                maksOrtalamaVadeGun: kural?.aktifMi ? kural.maksOrtalamaVadeGun : null,
                sonOdemeTarihi: kural?.aktifMi ? kural.sonOdemeTarihi : null,
                // Soruları cevaplarken önceki tarif gönderilir; yeniden AI'a gidilmez
                tarif: devam ? sonuc?.tarif : null,
                cevaplar: devam ? cevaplar : null,
            });
            const s: Sonuc = r.data;
            if (s.hata) { toast.error(s.hata); return; }
            // Cevaplarla devam edilirken tarif ilk yorumdan gelir — kaynak (AI / yerel) etiketi korunur
            setSonuc(devam && sonuc ? { ...s, kaynak: sonuc.kaynak } : s);
            // Önerilen / varsayılan cevaplar önceden doldurulur
            const yeni: Record<string, string> = {};
            s.sorular.forEach(q => { if (q.varsayilan) yeni[q.alan] = q.varsayilan; });
            setCevaplar(yeni);
            setAdim(s.sorular.some(q => q.zorunlu) || !s.kalemler ? 2 : 3);
        } catch { toast.error('Plan yorumlanamadı.'); }
        finally { setYukleniyor(false); }
    };

    // Tarifte tutarı açıkça verilen peşinat / ara ödemeler kilitli gelir; bir taksit değişince yalnız taksitler yeniden dağılır
    const kalemler: PlanKalemi[] = (sonuc?.kalemler ?? []).map(k => ({ ...k, kilitli: !/taksit|kalan ödeme/i.test(k.aciklama) }));
    const durum = planDurumu({ kalemler, toplam, sozlesmeTarihi, kural, listeFiyati, kullaniciID });
    const zorunluEksik = (sonuc?.sorular ?? []).some(q => q.zorunlu && !(cevaplar[q.alan] ?? '').trim());

    const giris: React.CSSProperties = { padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 9, fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };
    const btn = (ana = false): React.CSSProperties => ({
        padding: '9px 16px', borderRadius: 10, border: ana ? 'none' : '1.5px solid #e2e8f0', background: ana ? '#7c3aed' : '#fff',
        color: ana ? '#fff' : '#475569', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7,
    });
    const cip = (secili: boolean): React.CSSProperties => ({
        padding: '4px 11px', borderRadius: 20, border: `1.5px solid ${secili ? '#7c3aed' : '#e2e8f0'}`, background: secili ? '#f5f3ff' : '#fff',
        color: secili ? '#6d28d9' : '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    });

    return (
        <div onClick={onKapat} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 18, width: 'min(860px, 96vw)', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(15,23,42,.3)' }}>
                {/* Başlık + adımlar */}
                <div style={{ padding: '18px 22px 12px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <i className="fas fa-magic" style={{ color: '#7c3aed', fontSize: 18 }}></i>
                        <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', flex: 1 }}>AI ile ödeme planı</div>
                        <span style={{ fontSize: 12, color: '#64748b' }}>Sözleşme: <b>{tl(toplam)}</b> · {tarihTr(sozlesmeTarihi)}</span>
                        <button onClick={onKapat} style={{ border: 'none', background: 'none', fontSize: 18, color: '#94a3b8', cursor: 'pointer' }}><i className="fas fa-times"></i></button>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                        {['Tarif', 'Eksik bilgiler', 'Önizleme'].map((a, i) => {
                            const no = (i + 1) as 1 | 2 | 3;
                            const aktif = adim === no, gecti = adim > no;
                            return (
                                <div key={a} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 7, padding: '6px 10px', borderRadius: 9, background: aktif ? '#f5f3ff' : '#f8fafc', color: aktif ? '#6d28d9' : gecti ? '#16a34a' : '#94a3b8', fontSize: 12.5, fontWeight: 700 }}>
                                    <span style={{ width: 20, height: 20, borderRadius: '50%', background: aktif ? '#7c3aed' : gecti ? '#16a34a' : '#e2e8f0', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>
                                        {gecti ? <i className="fas fa-check"></i> : no}
                                    </span>{a}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div style={{ padding: '16px 22px', overflowY: 'auto', flex: 1 }}>
                    {adim === 1 && (
                        <>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 6 }}>Ödeme planını yazıyla tarif edin</div>
                            <textarea autoFocus value={metin} onChange={e => setMetin(e.target.value)} rows={4} style={{ ...giris, resize: 'vertical' }}
                                placeholder="Ör. %30 peşin, 6. ayda 500 bin ara ödeme, kalanı 18 ay senetle"
                                onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && metin.trim()) sorgula(false); }} />
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                                <span style={{ fontSize: 11.5, color: '#94a3b8', alignSelf: 'center' }}>Örnekler:</span>
                                {ORNEKLER.map(o => <button key={o} type="button" style={cip(metin === o)} onClick={() => setMetin(o)}>{o}</button>)}
                            </div>
                            <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 10 }}>
                                Peşinat, ara ödemeler (ay / tutar / %), taksit sayısı ya da aylık tutar, taksit aralığı, ilk taksit zamanı ve ödeme türü
                                (nakit, havale, senet, çek) yazılabilir. Eksik kalan bilgi bir sonraki adımda sorulur.
                                {kural?.aktifMi && kural.maksOrtalamaVadeGun != null && <> Projede ortalama vade en fazla <b>{kural.maksOrtalamaVadeGun} gün</b>.</>}
                            </div>
                        </>
                    )}

                    {adim >= 2 && sonuc && (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: sonuc.kaynak === 'ai' ? '#faf5ff' : '#f0f9ff', border: `1px solid ${sonuc.kaynak === 'ai' ? '#e9d5ff' : '#bae6fd'}`, borderRadius: 10, padding: '9px 12px', marginBottom: 14, fontSize: 12.5 }}>
                            <i className={`fas ${sonuc.kaynak === 'ai' ? 'fa-magic' : 'fa-spell-check'}`} style={{ color: sonuc.kaynak === 'ai' ? '#7c3aed' : '#0369a1', marginTop: 2 }}></i>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 700, color: '#334155' }}>"{metin}"</div>
                                <div style={{ color: '#475569', marginTop: 2 }}>{sonuc.aciklama}</div>
                                {sonuc.kaynak === 'kural' && <div style={{ color: '#94a3b8', marginTop: 2 }}>{sonuc.aiAktif ? 'AI yanıt vermedi; yerel kurallarla yorumlandı.' : 'Yapay zekâ ayarı kapalı; yerel kurallarla yorumlandı.'}</div>}
                            </div>
                        </div>
                    )}

                    {adim === 2 && sonuc && (
                        <>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 10 }}>Planı tamamlamak için birkaç bilgi gerekiyor</div>
                            {sonuc.sorular.map(q => (
                                <div key={q.alan} style={{ marginBottom: 14 }}>
                                    <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 600, marginBottom: 6 }}>
                                        {q.soru}{!q.zorunlu && <span style={{ color: '#94a3b8', fontWeight: 400 }}> (isteğe bağlı)</span>}
                                    </div>
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                        {q.secenekler.map(o => (
                                            <button key={o} type="button" style={cip(cevaplar[q.alan] === o)} onClick={() => setCevaplar(c => ({ ...c, [q.alan]: o }))}>
                                                {q.alan.endsWith('odemeTuru') ? (TUR_ADI[o] ?? o) : o}
                                            </button>
                                        ))}
                                        <input style={{ ...giris, width: 180 }} value={cevaplar[q.alan] ?? ''}
                                            placeholder={q.tip === 'tutar' ? 'Ör. %20 ya da 750 bin' : q.tip === 'sayi' ? 'Sayı' : 'Değer'}
                                            onChange={e => setCevaplar(c => ({ ...c, [q.alan]: e.target.value }))} />
                                    </div>
                                </div>
                            ))}
                        </>
                    )}

                    {adim === 3 && sonuc?.kalemler && (
                        <>
                            {sonuc.varsayimlar.length > 0 && (
                                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
                                    <i className="fas fa-info-circle" style={{ marginRight: 5 }}></i>Varsayımlar: {sonuc.varsayimlar.join(' ')}
                                </div>
                            )}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
                                {[['Kalem', String(kalemler.length)], ['Toplam', tl(durum.planToplam)], ['Fark', tl(durum.fark)], ['Ortalama vade', `${durum.ortalamaVade} gün`]].map(([e, d]) => (
                                    <div key={e} style={{ background: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: 10, padding: '8px 12px' }}>
                                        <div style={{ fontSize: 10.5, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>{e}</div>
                                        <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{d}</div>
                                    </div>
                                ))}
                            </div>
                            <div style={{ marginBottom: 12 }}><FiyatKontrolUyari sonuc={durum.kontrol} engelMetni="Bu planla sözleşme belgeleri yazdırılamaz." onAraliklar={() => setAraliklarAcik(true)} /></div>
                            <div style={{ border: '1px solid #f1f5f9', borderRadius: 10, maxHeight: 280, overflowY: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                                    <thead><tr style={{ background: '#f8fafc', color: '#94a3b8', fontSize: 10.5, textTransform: 'uppercase', position: 'sticky', top: 0 }}>
                                        <th style={{ padding: '7px 10px', textAlign: 'left' }}>#</th><th style={{ padding: '7px 10px', textAlign: 'left' }}>Vade</th>
                                        <th style={{ padding: '7px 10px', textAlign: 'left' }}>Açıklama</th><th style={{ padding: '7px 10px', textAlign: 'left' }}>Tür</th>
                                        <th style={{ padding: '7px 10px', textAlign: 'right' }}>Tutar</th>
                                    </tr></thead>
                                    <tbody>
                                        {kalemler.map((k, i) => (
                                            <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '6px 10px', color: '#cbd5e1' }}>{i + 1}</td>
                                                <td style={{ padding: '6px 10px' }}>{tarihTr(k.vadeTarihi)}</td>
                                                <td style={{ padding: '6px 10px' }}>{k.aciklama}</td>
                                                <td style={{ padding: '6px 10px' }}>{TUR_ADI[k.odemeTuru] ?? k.odemeTuru}</td>
                                                <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700 }}>{tl(k.miktar)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>

                <div style={{ padding: '12px 22px 18px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    {adim > 1 && <button type="button" style={{ ...btn(), marginRight: 'auto' }} onClick={() => setAdim(1)}><i className="fas fa-pen"></i>Tarifi değiştir</button>}
                    <button type="button" style={btn()} onClick={onKapat}>Vazgeç</button>
                    {adim === 1 && (
                        <button type="button" style={btn(true)} disabled={!metin.trim() || yukleniyor} onClick={() => sorgula(false)}>
                            <i className={`fas ${yukleniyor ? 'fa-spinner fa-spin' : 'fa-magic'}`}></i>Planı oluştur
                        </button>
                    )}
                    {adim === 2 && (
                        <button type="button" style={btn(true)} disabled={zorunluEksik || yukleniyor} onClick={() => sorgula(true)}>
                            <i className={`fas ${yukleniyor ? 'fa-spinner fa-spin' : 'fa-arrow-right'}`}></i>Devam
                        </button>
                    )}
                    {adim === 3 && (
                        <button type="button" style={btn(true)} onClick={() => {
                            if (mevcutKalemSayisi > 0 && !window.confirm('Mevcut ödeme planı silinip bu planla değiştirilsin mi?')) return;
                            onUygula(kalemler);
                        }}>
                            <i className="fas fa-check"></i>Planı uygula
                        </button>
                    )}
                </div>
            </div>
            {araliklarAcik && kural && (
                <div onClick={e => e.stopPropagation()}>
                    <FiyatAraliklariModal kural={kural} listeFiyati={listeFiyati ?? undefined} ortalamaVade={durum.ortalamaVade} onClose={() => setAraliklarAcik(false)} />
                </div>
            )}
        </div>
    );
};

export default AiOdemePlaniSihirbazi;
